import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import ImpersonationBanner from './components/ImpersonationBanner';
import Login from './pages/Login';
import PublicSign from './pages/PublicSign';
import Dashboard from './pages/Dashboard';
import SendContract from './pages/SendContract';
import AddRecord from './pages/AddRecord';
import UpdateRecord from './pages/UpdateRecord';
import JobTracker from './pages/JobTracker';
import SalesReport from './pages/SalesReport';
import Contractors from './pages/Contractors';
import Staff from './pages/Staff';
import FormsList from './pages/FormsList';
import StaffReport from './pages/StaffReport';
import Collection from './pages/Collection';
import AttachmentsPortal from './pages/AttachmentsPortal';
import Users from './pages/Users';
import DataSafety from './pages/DataSafety';
import Training from './pages/Training';
import TrainingLesson from './pages/TrainingLesson';
import TrainingQuiz from './pages/TrainingQuiz';
import TrainingAdmin from './pages/TrainingAdmin';
import QuickBooks from './pages/QuickBooks';
import Presentations from './pages/Presentations';
import IDCards from './pages/IDCards';
import CalendarPage from './pages/CalendarPage';
import MyJobs from './pages/MyJobs';
import ContractorPortal from './pages/ContractorPortal';
import ChangeOrders from './pages/ChangeOrders';
import ContractorPackets from './pages/ContractorPackets';
import CanvasserPayroll from './pages/CanvasserPayroll';
import JobCalculator from './pages/JobCalculator';
import PaintingCalculatorPage from './pages/PaintingCalculatorPage';
import CompletedProjects from './pages/CompletedProjects';
import ObjectionRoom from './pages/ObjectionRoom';
import Testimonials from './pages/Testimonials';
import InvestmentReview from './pages/InvestmentReview';
import PerformanceBoard from './pages/PerformanceBoard';

function ProtectedLayout({ children }) {
  const { isAuthenticated, isAttachments, isStaff, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isAttachments) return <Navigate to="/attachments-portal" replace />;
  if (isStaff) return <Navigate to="/my-jobs" replace />;
  if (isContractor) return <Navigate to="/contractor-portal" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function StaffLayout({ children }) {
  const { isAuthenticated, isStaff } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function ContractorLayout({ children }) {
  const { isAuthenticated, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isContractor) return <Navigate to="/" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function SharedLayout({ children }) {
  // Accessible to any authenticated, non-portal user (staff reps AND admins)
  const { isAuthenticated, isAttachments, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isAttachments) return <Navigate to="/attachments-portal" replace />;
  if (isContractor) return <Navigate to="/contractor-portal" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sign/:token" element={<PublicSign />} />
          <Route path="/attachments-portal" element={<AttachmentsPortal />} />
          <Route path="/my-jobs" element={<StaffLayout><MyJobs /></StaffLayout>} />
          <Route path="/contractor-portal" element={<ContractorLayout><ContractorPortal /></ContractorLayout>} />
          <Route path="/" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
          <Route path="/tracker" element={<ProtectedLayout><JobTracker /></ProtectedLayout>} />
          <Route path="/send-contract" element={<SharedLayout><SendContract /></SharedLayout>} />
          <Route path="/staff-report" element={<ProtectedLayout><StaffReport /></ProtectedLayout>} />
          <Route path="/sales" element={<ProtectedLayout><SalesReport /></ProtectedLayout>} />
          <Route path="/collection" element={<ProtectedLayout><Collection /></ProtectedLayout>} />
          <Route path="/calendar" element={<ProtectedLayout><CalendarPage /></ProtectedLayout>} />
          <Route path="/change-orders" element={<ProtectedLayout><ChangeOrders /></ProtectedLayout>} />
          <Route path="/contractor-packets" element={<ProtectedLayout><AdminOnly><ContractorPackets /></AdminOnly></ProtectedLayout>} />
          <Route path="/canvasser-payroll" element={<ProtectedLayout><AdminOnly><CanvasserPayroll /></AdminOnly></ProtectedLayout>} />
          <Route path="/calculator" element={<SharedLayout><JobCalculator /></SharedLayout>} />
          <Route path="/painting-calculator" element={<SharedLayout><PaintingCalculatorPage /></SharedLayout>} />
          <Route path="/projects" element={<SharedLayout><CompletedProjects /></SharedLayout>} />
          <Route path="/objections" element={<SharedLayout><ObjectionRoom /></SharedLayout>} />
          <Route path="/testimonials" element={<SharedLayout><Testimonials /></SharedLayout>} />
          <Route path="/investment-review" element={<SharedLayout><InvestmentReview /></SharedLayout>} />
          <Route path="/board" element={<SharedLayout><PerformanceBoard /></SharedLayout>} />
          <Route path="/add" element={<ProtectedLayout><AdminOnly><AddRecord /></AdminOnly></ProtectedLayout>} />
          <Route path="/update" element={<ProtectedLayout><AdminOnly><UpdateRecord /></AdminOnly></ProtectedLayout>} />
          <Route path="/contractors" element={<ProtectedLayout><AdminOnly><Contractors /></AdminOnly></ProtectedLayout>} />
          <Route path="/staff" element={<ProtectedLayout><AdminOnly><Staff /></AdminOnly></ProtectedLayout>} />
          <Route path="/forms" element={<ProtectedLayout><AdminOnly><FormsList /></AdminOnly></ProtectedLayout>} />
          <Route path="/users" element={<ProtectedLayout><AdminOnly><Users /></AdminOnly></ProtectedLayout>} />
          <Route path="/data-safety" element={<ProtectedLayout><AdminOnly><DataSafety /></AdminOnly></ProtectedLayout>} />
          <Route path="/training" element={<SharedLayout><Training /></SharedLayout>} />
          <Route path="/training/lesson/:id" element={<SharedLayout><TrainingLesson /></SharedLayout>} />
          <Route path="/training/quiz/:id" element={<SharedLayout><TrainingQuiz /></SharedLayout>} />
          <Route path="/presentations" element={<SharedLayout><Presentations /></SharedLayout>} />
          <Route path="/id-cards" element={<ProtectedLayout><AdminOnly><IDCards /></AdminOnly></ProtectedLayout>} />
          <Route path="/training-admin" element={<ProtectedLayout><AdminOnly><TrainingAdmin /></AdminOnly></ProtectedLayout>} />
          <Route path="/quickbooks" element={<ProtectedLayout><AdminOnly><QuickBooks /></AdminOnly></ProtectedLayout>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
